import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";

import { WirelessHealthDetail } from "../../../apis/networkdashboard/rest/models";
import { KPI_ITEMS_AEROHIVE, KPI_ITEMS_ARUBA, KpiItem } from "../../../helpers/kpi/kpi-item";
import { KpiData } from "./components/kpi-data/kpi-data";

@Component({
  selector: "kpi-table",
  templateUrl: "kpi-table.html",
  styleUrls: ["kpi-table.scss"],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [KpiData],
})
export class KpiTableComponent {
  readonly kpis = input.required<WirelessHealthDetail>();
  readonly vendor = input.required<string>();

  protected kpiItems = computed(() => {
    const vendor: string = this.vendor().toLowerCase().trim().replace(" ", "-");
    if (vendor === "aerohive") {
      return KPI_ITEMS_AEROHIVE;
    } else if (vendor === "aruba") {
      return KPI_ITEMS_ARUBA;
    } else {
      console.warn(`Unknown wifi vendor: ${vendor}.`);
      return [];
    }
  });

  protected supportsTwofourGhz = computed(() => !this.hasAnyBandData() || this.hasBandData("twofour"));
  protected supportsFiveGhz = computed(() => !this.hasAnyBandData() || this.hasBandData("five"));
  protected supportsSixGhz = computed(() => !this.hasAnyBandData() || this.hasBandData("six"));

  protected kpiRowsTwofour = computed(() => this.getKpiData("twofour"));
  protected kpiRowsFive = computed(() => this.getKpiData("five"));
  protected kpiRowsSix = computed(() => this.getKpiData("six"));

  private hasBandData(frequency: "twofour" | "five" | "six"): boolean {
    const data = this.kpis();
    return (
      data?.today?.[frequency] != null || data?.last3Days?.[frequency] != null || data?.lastWeek?.[frequency] != null
    );
  }

  private hasAnyBandData(): boolean {
    return this.hasBandData("twofour") || this.hasBandData("five") || this.hasBandData("six");
  }

  private getKpiData(frequency: "twofour" | "five" | "six") {
    const kpiMoments = {
      today: 1,
      last3Days: 3,
      lastWeek: 7,
    };
    const hasAnyBandData = this.hasAnyBandData();

    return Object.entries(kpiMoments).map(([momentName, numDays]) => {
      if (!hasAnyBandData) {
        return { days: numDays, values: [] };
      }

      const extractKpiMoment = (kpiItem: KpiItem) => {
        const rawValue = this.kpis()[momentName][frequency][kpiItem.key];
        const value = parseFloat(rawValue) * (kpiItem.multiplier ?? 1);
        return { value, kpi: kpiItem };
      };

      const kpiMomentData = this.kpiItems().map(extractKpiMoment);
      return { days: numDays, values: kpiMomentData };
    });
  }
}
